import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Checkbox,
	EmptyState,
	FileTypeIcon,
	Input,
	ModalBase,
} from '@meddihub/ui';
import type { PickedHealthDocument } from '@meddihub/mzcr-ui';
import downloadHealthDocuments from '../Services/downloadHealthDocuments';
import {
	useDocumentationFilesQuery,
	useDocumentationFoldersQuery,
} from '../../../graphql/graphql';
import useDateFormatter from '../../../Hooks/useDateFormatter';
import useTranslation from '../../../Hooks/useTranslation';
import useNotification from '../../../Hooks/useNotification.hook';
import { HealthDocumentationFileRef } from '../../Ecard/Interfaces/HealthDocumentationFileRef.interface';
import { toHealthDocumentationFileRef } from '../../Ecard/Services/healthDocumentationFile.service';
import { formatFilesCount } from '../Utils/format.util';
// @ts-ignore
import styles from './PickFromHealthDocumentationModal.module.scss';
import ListSkeleton from '../../Base/Components/ListSkeleton';
import { FolderIcon, PlusIcon, SearchIcon } from './icons';

interface Props {
	patientId: string;
	/** `null` when the user closed the dialog without adding anything. */
	onClose: (documents: PickedHealthDocument[] | null) => void;
}

const ALL_FILES = 'all';

/**
 * Picks files from the patient's health documentation for a new Dočasné úložiště package
 * (design D5) – folders on the left, their files on the right, files of several folders at once.
 */
const PickFromHealthDocumentationModal: React.FC<Props> = ({
	patientId,
	onClose,
}) => {
	const { t } = useTranslation();
	const { showNotificationAlert } = useNotification();
	const { formatDate } = useDateFormatter();
	const [folderId, setFolderId] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [picked, setPicked] = useState<HealthDocumentationFileRef[]>([]);
	const [isAdding, setIsAdding] = useState(false);

	const foldersQuery = useDocumentationFoldersQuery(
		{ userId: patientId },
		{ refetchOnMountOrArgChange: true },
	);
	const folders = useMemo(
		() =>
			(foldersQuery.data?.documentationFolders?.data ?? [])
				.filter(Boolean)
				.map(folder => ({
					id: folder!.id as string,
					name: folder!.name ?? '',
					filesCount: folder!.filesCount ?? 0,
				})),
		[foldersQuery.data],
	);

	useEffect(() => {
		if (!folderId && folders.length > 0) {
			setFolderId(folders[0].id);
		}
	}, [folders, folderId]);

	// "Zdravotnická dokumentace" on top of the folders = all the patient's files
	const totalCount = folders.reduce(
		(sum, folder) => sum + folder.filesCount,
		0,
	);
	const isAll = folderId === ALL_FILES;

	const filesQuery = useDocumentationFilesQuery(
		{
			userId: patientId,
			folderId:
				search.trim() || folderId === ALL_FILES
					? undefined
					: folderId ?? undefined,
			fulltext: search.trim() || undefined,
			take: 100,
			grouped: false,
		},
		{ skip: !folderId && !search.trim() },
	);
	const files = filesQuery.data?.documentationFiles?.data ?? [];
	const folderName = (id?: string) =>
		folders.find(folder => folder.id === id)?.name ?? '';

	const isPicked = (id: string) => picked.some(file => file.id === id);
	const togglePicked = (file: (typeof files)[number], checked: boolean) =>
		setPicked(current =>
			checked
				? [
						...current,
						toHealthDocumentationFileRef(file, folderName(file.folderId)),
				  ]
				: current.filter(item => item.id !== file.id),
		);

	const handleAdd = async () => {
		setIsAdding(true);
		try {
			const documents = await downloadHealthDocuments(patientId, picked);
			onClose(documents);
		} catch (e) {
			console.error(e);
			showNotificationAlert({
				content: t('component.mzcr.pickModal.loadFailed'),
			});
		} finally {
			setIsAdding(false);
		}
	};

	const renderFolder = (
		id: string,
		name: string,
		count: number,
		isRoot = false,
	) => {
		const isSelected = !search.trim() && folderId === id;
		return (
			<li key={id}>
				<button
					type='button'
					className={[
						styles.folder,
						isRoot ? styles.folderRoot : '',
						isSelected ? styles.folderSelected : '',
					].join(' ')}
					aria-current={isSelected || undefined}
					onClick={() => {
						setSearch('');
						setFolderId(id);
					}}>
					<FolderIcon open={isRoot || isSelected} size={15} />
					<span className={styles.folderName}>{name}</span>
					<span className={styles.count}>{count}</span>
				</button>
			</li>
		);
	};

	return (
		<ModalBase
			open
			onClose={() => onClose(null)}
			size='xl'
			className={styles.modal}>
			<ModalBase.Header
				title={t('component.mzcr.pickModal.title')}
				className={styles.header}
			/>
			<div className={styles.search}>
				<Input
					aria-label={t('component.mzcr.pickModal.searchLabel')}
					placeholder={t('component.mzcr.pickModal.searchPlaceholder')}
					prefix={<SearchIcon />}
					value={search}
					onChange={event => setSearch(event.target.value)}
				/>
			</div>
			<div className={styles.layout}>
				<nav
					className={styles.folders}
					aria-label={t('component.mzcr.pickModal.foldersLabel')}>
					{foldersQuery.isLoading ? (
						<ListSkeleton rows={6} compact />
					) : (
						<ul className={styles.folderList}>
							{renderFolder(
								ALL_FILES,
								t('component.mzcr.healthDocumentation'),
								totalCount,
								true,
							)}
							{folders.map(folder =>
								renderFolder(folder.id, folder.name, folder.filesCount),
							)}
						</ul>
					)}
				</nav>
				<div className={styles.files}>
					<div className={styles.filesHead} aria-hidden='true'>
						<span className={styles.checkCol} />
						<span className={styles.nameCol}>
							{t('component.mzcr.pickModal.columnName')}
						</span>
						<span className={styles.dateCol}>
							{t('component.mzcr.pickModal.columnDate')}
						</span>
					</div>
					{filesQuery.isFetching ? (
						<ListSkeleton rows={6} compact />
					) : files.length === 0 ? (
						<EmptyState title={t('component.mzcr.pickModal.emptyTitle')}>
							{search.trim()
								? t('component.mzcr.pickModal.emptySearch')
								: t('component.mzcr.pickModal.emptyFolder')}
						</EmptyState>
					) : (
						<ul className={styles.fileList}>
							{files.map(file => {
								const fileLabel = file.displayName || file.name || '';
								const checked = isPicked(file.id);
								return (
									<li
										key={file.id}
										className={`${styles.fileRow}${
											checked ? ` ${styles.fileRowChecked}` : ''
										}`}>
										<span className={styles.checkCol}>
											<Checkbox
												aria-label={t('component.mzcr.pickModal.pickFile', {
													name: fileLabel,
												})}
												checked={checked}
												onChange={value => togglePicked(file, value)}
											/>
										</span>
										<span className={styles.nameCol}>
											<FileTypeIcon
												fileName={`${fileLabel}.${
													file.file?.extension || file.extension || ''
												}`}
												size='sm'
											/>
											<span className={styles.fileTexts}>
												<span className={styles.fileName}>{fileLabel}</span>
												{(search.trim() || isAll) && (
													<span className={styles.fileFolder}>
														{folderName(file.folderId)}
													</span>
												)}
											</span>
										</span>
										<span className={styles.dateCol}>
											{file.createdAt ? formatDate(file.createdAt) : ''}
										</span>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</div>
			<ModalBase.Footer className={styles.footer}>
				<Button
					variant='success'
					icon={<PlusIcon />}
					disabled={picked.length === 0}
					isLoading={isAdding}
					onClick={handleAdd}>
					{picked.length > 0
						? t('component.mzcr.pickModal.addCount', {
								count: String(picked.length),
						  })
						: t('component.mzcr.pickModal.add')}
				</Button>
				{picked.length > 0 && (
					<span className={styles.note}>
						{t('component.mzcr.pickModal.picked', {
							files: formatFilesCount(picked.length, t),
						})}{' '}
						·{' '}
						{picked.map(file => `${file.name} (${file.folderName})`).join(', ')}
					</span>
				)}
			</ModalBase.Footer>
		</ModalBase>
	);
};

export default PickFromHealthDocumentationModal;
